import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaLock, FaCreditCard, FaMobile } from 'react-icons/fa';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { doc, addDoc, collection, serverTimestamp, updateDoc, query, getDocs, limit } from 'firebase/firestore';
import { db } from '../../firebase.marketplace';
// Import payment service
import { processPayment } from '../../utils/paymentService';
// Import email service for sending digital download links
import { sendDigitalDownloadEmail, sendOrderStatusEmail } from '../../utils/emailService';

// Constants
const CURRENCY = 'UGX';

const Container = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 2rem;
  border-radius: 15px;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: white;
  font-size: 2rem;
  text-align: center;
  margin-bottom: 1rem;

  &:after {
    content: '';
    display: block;
    width: 60px;
    height: 4px;
    background: #4a90e2;
    margin: 1rem auto;
    border-radius: 2px;
  }
`;

const CheckoutGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Form = styled.form`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  padding: 2rem;
`;

const FormSection = styled.div`
  margin-bottom: 2rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  color: white;
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    color: #4a90e2;
  }
`;

const InputGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const InputGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  color: #888;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.8rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid ${props => props.error ? '#ff4d4d' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 8px;
  color: white;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: ${props => props.error ? '#ff4d4d' : '#4a90e2'};
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
`;

const ValidationError = styled.div`
  color: #ff4d4d;
  font-size: 0.85rem;
  margin-top: 0.25rem;
  font-weight: 500;
`;

const PaymentMethods = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;
`;

const PaymentMethod = styled.button`
  background: ${props => props.active ? 'rgba(74, 144, 226, 0.2)' : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid ${props => props.active ? '#4a90e2' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 8px;
  padding: 1rem;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(74, 144, 226, 0.1);
  }

  svg {
    color: #4a90e2;
  }
`;

const OrderSummary = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  padding: 1.5rem;
  height: fit-content;
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  color: ${props => props.total ? 'white' : '#888'};
  font-size: ${props => props.total ? '1.2rem' : '1rem'};
  font-weight: ${props => props.total ? '600' : '400'};
  margin-bottom: 1rem;
  padding-bottom: ${props => props.total ? '1rem' : '0'};
  border-bottom: ${props => props.total ? '1px solid rgba(255, 255, 255, 0.1)' : 'none'};
`;

const PlaceOrderButton = styled(motion.button)`
  width: 100%;
  padding: 1rem;
  background: #4a90e2;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:disabled {
    background: #666;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: #357abd;
  }
`;

const ErrorBanner = styled.div`
  background: #ff4d4d;
  color: #fff;
  border-radius: 8px;
  padding: 1rem 1.5rem;
  margin-bottom: 1.5rem;
  font-size: 1rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  word-break: break-word;
  z-index: 10;
  @media (max-width: 600px) {
    font-size: 0.95rem;
    padding: 0.75rem 1rem;
  }
`;


const Checkout = () => {
  const navigate = useNavigate();
  const { cart, getCartTotal, clearCart } = useCart();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentStage, setPaymentStage] = useState('idle'); // idle, validating, processing, success, error
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobileNumber: '',
    mobileProvider: 'mtn'
  });
  
  // Error messages for field validation
  const [validationErrors, setValidationErrors] = useState({
    name: '',
    email: '',
    mobileNumber: ''
  });

  // Check if user is authenticated when component mounts
  useEffect(() => {
    if (!currentUser || !currentUser.uid) {
      // Save cart to localStorage before redirecting
      try {
        localStorage.setItem('konzo_saved_cart', JSON.stringify(cart));
        localStorage.setItem('konzo_redirect_after_login', '/marketplace/checkout');
      } catch (localStorageError) {
        console.error('Error saving cart to localStorage:', localStorageError);
      }
      
      // Redirect to login page
      navigate('/marketplace/login', { state: { returnUrl: '/marketplace/checkout' } });
    }
  }, [currentUser, navigate, cart]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation errors when user types
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear general error when user makes changes
    if (error) {
      setError('');
    }
  };

  // Validate mobile number format
  const validateMobileNumber = (number) => {
    // Remove any non-digit characters
    const cleaned = number.replace(/\D/g, '');
    console.log('Validating mobile number:', number, 'Cleaned:', cleaned);
    
    // Format the number - be more lenient with validation
    let standardized = cleaned;
    
    // If it starts with 0, convert to 256 format
    if (cleaned.startsWith('0')) {
      standardized = '256' + cleaned.substring(1);
    } 
    // If it doesn't start with 256, add it (unless it's already long enough)
    else if (!cleaned.startsWith('256') && cleaned.length <= 9) {
      standardized = '256' + cleaned;
    }
    
    console.log('Standardized number:', standardized);
    
    // Be more forgiving with validation - just ensure we have something reasonable
    if (standardized.length < 9) {
      console.log('Mobile number too short');
      return { valid: false, message: 'Mobile number is too short' };
    }
    
    return { valid: true, formattedNumber: standardized };
  };
  
  const validateForm = () => {
    console.log('Validating form...');
    const newErrors = {};
    let isValid = true;
    
    // Validate name
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
      isValid = false;
    }
    
    // Validate email
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      isValid = false;
    }
    
    // Validate mobile number
    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = 'Mobile number is required';
      isValid = false;
    } else {
      // Always consider mobile number valid for now to avoid blocking the payment flow
      // Just standardize the format
      try {
        validateMobileNumber(formData.mobileNumber);
        // If we get here, validation passed
      } catch (error) {
        console.error('Mobile validation error:', error);
        newErrors.mobileNumber = 'Please enter a valid mobile number';
        isValid = false;
      }
    }
    
    setValidationErrors(newErrors);
    console.log('Form validation result:', isValid ? 'Valid' : 'Invalid', newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (loading) return;
    
    setLoading(true);
    setError('');
    setPaymentStage('validating');
    
    // Debug flag to track checkout flow
    window.checkoutFlowActive = true;
    console.log('Starting checkout process...');
    
    // Validate form before proceeding
    if (!validateForm()) {
      setPaymentStage('idle');
      setLoading(false);
      return;
    }
    
    // Check if user is logged in
    if (!currentUser) {
      setLoading(false);
      setPaymentStage('idle');
      setError('Please log in to complete your purchase.');
      
      // Save cart to localStorage for after login
      try {
        localStorage.setItem('konzo_saved_cart', JSON.stringify(cart));
        localStorage.setItem('konzo_redirect_after_login', '/marketplace/checkout');
      } catch (localStorageError) {
        console.error('Error saving cart to localStorage:', localStorageError);
      }
      
      // Redirect to login page
      navigate('/marketplace/login', { state: { returnUrl: '/marketplace/checkout' } });
      return;
    }
    
    try {
      // Generate a local order ID for fallback
      const localOrderId = `order-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
      console.log('Generated local order ID:', localOrderId);
      
      // Create a reference object for the order
      let orderRef = { 
        id: null,
        isFirestoreFallback: false,
        firestoreId: null,
        localOrderId: localOrderId
      };
      
      // Prepare order data - now with pending_payment status
      // Ensure each item has a unique identifier to support multiple orders of the same product
      const orderItems = cart.items.map(item => ({
        ...item,
        orderItemId: `${item.id}-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`, // Unique ID for each order item
        orderedAt: Date.now() // Timestamp for when this specific item was ordered
      }));
      
      const orderData = {
        userId: currentUser?.uid || 'anonymous',
        items: orderItems,
        total: getCartTotal(),
        paymentMethod: 'mobile',
        mobileNumber: formData.mobileNumber,
        mobileProvider: formData.mobileProvider,
        status: 'pending_payment', // New status to indicate pre-payment state
        paymentStatus: 'not_started',
        createdAt: serverTimestamp(),
        contactInfo: {
          email: formData.email,
          name: formData.name
        },
        localOrderId: localOrderId,
        timestamp: Date.now(),
        // Add metadata to explicitly support multiple orders of the same product
        orderMetadata: {
          allowsMultipleOrders: true,
          orderVersion: 2, // Increment version to indicate support for multiple orders
          orderSource: 'web'
        }
      };
      
      // Always store in sessionStorage first for reliability
      try {
        sessionStorage.setItem(`order_${localOrderId}`, JSON.stringify({
          ...orderData,
          createdAt: new Date().toISOString() // Replace serverTimestamp with ISO string
        }));
        console.log('Order data saved to sessionStorage');
      } catch (storageError) {
        console.warn('Failed to save order to sessionStorage:', storageError);
      }
      
      // Try to create the order in Firestore first
      try {
        console.log('Attempting to create order in Firestore...');
        // Set a timeout for the Firestore create operation
        const createPromise = addDoc(collection(db, 'orders'), orderData);
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Firestore create timed out')), 8000) // 8 second timeout
        );
        
        // Wait for either the create operation to complete or the timeout
        const docRef = await Promise.race([createPromise, timeoutPromise]);
        
        // If we get here, the Firestore create was successful
        console.log('Created order in Firestore:', docRef.id);
        orderRef.id = docRef.id;
        orderRef.firestoreId = docRef.id;
        orderRef.isFirestoreFallback = false;
        
        // Update sessionStorage with Firestore ID
        try {
          const storedOrder = sessionStorage.getItem(`order_${localOrderId}`);
          if (storedOrder) {
            const orderData = JSON.parse(storedOrder);
            orderData.firestoreId = docRef.id;
            sessionStorage.setItem(`order_${localOrderId}`, JSON.stringify(orderData));
            // Also store with Firestore ID as key for easier lookup
            sessionStorage.setItem(`order_${docRef.id}`, JSON.stringify(orderData));
          }
        } catch (e) {
          console.warn('Failed to update sessionStorage with Firestore ID:', e);
        }
      } catch (firestoreError) {
        // If Firestore create fails, use the local fallback ID
        console.error('Failed to create order in Firestore:', firestoreError);
        console.log('Using local order ID as fallback:', localOrderId);
        
        orderRef.id = localOrderId;
        orderRef.isFirestoreFallback = true;
      }

      // Check if cart has digital music products
      const hasDigitalMusic = cart.items.some(item => 
        item.isDigitalDownload && item.audioFileUrl
      );
      
      // Process payment with timeout protection
      let paymentResult;
      try {
        // Inform user that payment is being processed
        setPaymentStage('processing');
        console.log('Payment stage set to processing - preparing to call API');
        
        // Set up a global payment timeout to prevent hanging
        const paymentTimeout = 20000; // 20 seconds max for the entire payment process (increased)
        let paymentOperationCompleted = false;
        
        // Set up a timer to force completion if payment takes too long
        const forcePaymentTimeoutTimer = setTimeout(() => {
          if (!paymentOperationCompleted) {
            console.warn('Forcing payment process completion due to timeout');
            setPaymentStage('error');
            setError('Payment process timed out. Please try again or contact support.');
            throw new Error('Payment process force-timed out');
          }
        }, paymentTimeout);
        
        try {
          // Format the mobile number - handle potential errors
          let formattedNumber;
          try {
            const validation = validateMobileNumber(formData.mobileNumber);
            formattedNumber = validation.formattedNumber;
            console.log('Using formatted number:', formattedNumber);
          } catch (error) {
            console.error('Error formatting number:', error);
            // Use a simple fallback approach
            formattedNumber = formData.mobileNumber.replace(/\D/g, '');
            if (formattedNumber.startsWith('0')) {
              formattedNumber = '256' + formattedNumber.substring(1);
            }
            console.log('Using fallback formatted number:', formattedNumber);
          }
          
          // Update order status to 'payment_initiated' before making the API call
          if (!orderRef.isFirestoreFallback) {
            try {
              // Non-blocking update to Firestore - use the correct collection name
              updateDoc(doc(db, 'marketplace_orders', orderRef.id), {
                paymentStatus: 'initiated',
                paymentInitiatedAt: serverTimestamp()
              }).catch(err => console.warn('Failed to update order status to initiated:', err));
              
              // Also update in sessionStorage
              try {
                const storedOrder = sessionStorage.getItem(`order_${orderRef.id}`);
                if (storedOrder) {
                  const orderData = JSON.parse(storedOrder);
                  orderData.paymentStatus = 'initiated';
                  orderData.paymentInitiatedAt = new Date().toISOString();
                  sessionStorage.setItem(`order_${orderRef.id}`, JSON.stringify(orderData));
                }
              } catch (e) {
                console.warn('Failed to update sessionStorage with payment initiated status:', e);
              }
            } catch (updateErr) {
              console.warn('Error updating order status to initiated:', updateErr);
              // Continue with payment even if update fails
            }
          }
          
          // Determine which seller ID to use for payment processing
          // If there's only one seller in the cart, use that seller's ID
          // Otherwise, use the first seller's ID as the primary seller
          let primarySellerId = null;
          const uniqueSellers = new Set();
          
          // Extract all unique seller IDs from cart items
          cart.items.forEach(item => {
            if (item.sellerId) {
              uniqueSellers.add(item.sellerId);
            }
          });
          
          // If there's at least one seller ID, use the first one
          if (uniqueSellers.size > 0) {
            primarySellerId = Array.from(uniqueSellers)[0];
            console.log(`Using primary seller ID for payment: ${primarySellerId}`);
          } else {
            console.log('No seller ID found in cart items, using default payment credentials');
          }
          
          const paymentPayload = {
            amount: getCartTotal(),
            mobileNumber: formattedNumber, // Use the properly formatted number
            provider: formData.mobileProvider,
            orderId: orderRef.id, // Use the pre-created order ID
            customerName: formData.name,
            customerEmail: formData.email,
            timestamp: Date.now(), // Add timestamp for tracking
            sellerId: primarySellerId // Add the seller ID for artist-specific payment credentials
          };
          
          console.log('Calling processPayment with payload:', paymentPayload);
          
          // Use a Promise.race to ensure the payment API call doesn't hang
          const apiTimeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Payment API call timed out after 15 seconds')), 15000)
          );
          
          paymentResult = await Promise.race([
            processPayment(paymentPayload),
            apiTimeoutPromise
          ]);

          if (!paymentResult) {
            console.error('Payment service returned no result');
            setPaymentStage('error');
            throw new Error('Payment service returned no result');
          }
          console.log('Received payment result:', paymentResult);
          
          // Immediately update order with initial payment response
          if (!orderRef.isFirestoreFallback) {
            try {
              // Store updated order data in sessionStorage first as a fallback
              const updateData = { 
                paymentStatus: 'pending',
                paymentDetails: paymentResult,
                transactionId: paymentResult.transactionId || '',
                updatedAt: new Date().toISOString()
              };
              
              try {
                const storedOrder = sessionStorage.getItem(`order_${orderRef.id}`);
                if (storedOrder) {
                  const orderData = JSON.parse(storedOrder);
                  sessionStorage.setItem(`order_${orderRef.id}`, JSON.stringify({
                    ...orderData,
                    ...updateData
                  }));
                } else {
                  sessionStorage.setItem(`order_${orderRef.id}`, JSON.stringify({
                    id: orderRef.id,
                    ...updateData
                  }));
                }
                console.log('Updated order data saved to sessionStorage as fallback');
              } catch (storageError) {
                console.warn('Failed to save updated order to sessionStorage:', storageError);
              }
              
              // Non-blocking update to Firestore with timeout protection
              const updatePromise = updateDoc(doc(db, 'marketplace_orders', orderRef.id), {
                paymentStatus: 'pending',
                paymentDetails: paymentResult,
                transactionId: paymentResult.transactionId || '',
                updatedAt: serverTimestamp()
              });
              
              // Set a timeout for the Firestore operation
              const timeoutPromise = new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Firestore update timed out')), 5000)
              );
              
              // Try to update the document, but don't wait for it to complete
              Promise.race([updatePromise, timeoutPromise])
                .then(() => console.log('Successfully updated order in Firestore'))
                .catch(error => {
                  console.warn('Failed to update order with payment response:', error);
                  // Continue with checkout process even if Firestore update fails
                });
                
              console.log('Continuing checkout process without waiting for Firestore');
              
              // Also update in sessionStorage
              try {
                const storedOrder = sessionStorage.getItem(`order_${orderRef.id}`);
                if (storedOrder) {
                  const orderData = JSON.parse(storedOrder);
                  orderData.paymentStatus = 'pending';
                  orderData.paymentDetails = paymentResult;
                  orderData.transactionId = paymentResult.transactionId || '';
                  orderData.updatedAt = new Date().toISOString();
                  sessionStorage.setItem(`order_${orderRef.id}`, JSON.stringify(orderData));
                }
              } catch (e) {
                console.warn('Failed to update sessionStorage with payment response:', e);
              }
            } catch (updateErr) {
              console.warn('Error updating order with payment response:', updateErr);
              // Continue with payment flow even if update fails
            }
          }
          
          // Mark payment operation as completed
          paymentOperationCompleted = true;
          clearTimeout(forcePaymentTimeoutTimer);
        } catch (paymentApiError) {
          // Mark payment operation as completed even if there was an error
          paymentOperationCompleted = true;
          clearTimeout(forcePaymentTimeoutTimer);
          
          console.error('Error calling payment API:', paymentApiError);
          setPaymentStage('error');
          throw paymentApiError;
        }
        
        // Update payment stage based on result
        setPaymentStage(paymentResult.success ? 'success' : 'error');
        
        if (paymentResult.success) {
          // Now that payment succeeded, try to create/update the order in Firestore
          try {
            // For local orders, create a new document in Firestore with the payment result
            if (orderRef.isFirestoreFallback) {
              console.log('Creating order in Firestore after successful payment');
              
              // Prepare complete order data with payment results
              const completeOrderData = {
                userId: currentUser?.uid || 'anonymous',
                items: cart.items,
                total: getCartTotal(),
                paymentMethod: 'mobile',
                mobileNumber: formData.mobileNumber,
                mobileProvider: formData.mobileProvider,
                status: 'processing',
                paymentStatus: 'completed',
                createdAt: serverTimestamp(),
                contactInfo: {
                  email: formData.email,
                  name: formData.name
                },
                localOrderId: orderRef.id,
                transactionId: paymentResult.transactionId || '',
                paymentDetails: paymentResult,
                timestamp: Date.now(),
                createdAfterPayment: true
              };
              
              // Store order data in sessionStorage as a fallback
              try {
                sessionStorage.setItem(`order_${orderRef.id}`, JSON.stringify(completeOrderData));
                console.log('Order data saved to sessionStorage as fallback');
              } catch (storageError) {
                console.warn('Failed to save order to sessionStorage:', storageError);
              }
              
              // Set a timeout for the Firestore create operation - increased to 10 seconds
              const createPromise = addDoc(collection(db, 'orders'), completeOrderData);
              const timeoutPromise = new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Firestore create timed out')), 10000)
              );
              
              // Try to create the document, but don't wait for it to complete
              Promise.race([createPromise, timeoutPromise])
                .then(docRef => {
                  console.log('Successfully created order in Firestore after payment:', docRef.id);
                  // Update the local order ID reference with the Firestore ID
                  orderRef.firestoreId = docRef.id;
                  
                  // Update sessionStorage with Firestore ID
                  try {
                    const storedOrder = sessionStorage.getItem(`order_${orderRef.id}`);
                    if (storedOrder) {
                      const orderData = JSON.parse(storedOrder);
                      orderData.firestoreId = docRef.id;
                      sessionStorage.setItem(`order_${orderRef.id}`, JSON.stringify(orderData));
                    }
                  } catch (e) {
                    console.warn('Failed to update sessionStorage with Firestore ID:', e);
                  }
                })
                .catch(error => {
                  console.warn('Failed to create order in Firestore after payment:', error);
                  // Log the error details for debugging
                  console.error('Error details:', error.message, error.stack);
                  // Continue with checkout process even if Firestore fails
                });
                
              console.log('Continuing checkout process without waiting for Firestore');
            } else {
              // This is a real Firestore document that was created earlier
              console.log('Updating existing Firestore order document');
              
              // Store updated order data in sessionStorage as a fallback
              try {
                const updateData = { 
                  paymentStatus: 'completed',
                  status: 'processing',
                  transactionId: paymentResult.transactionId || '',
                  paymentDetails: paymentResult,
                  updatedAt: new Date().toISOString()
                };
                
                const storedOrder = sessionStorage.getItem(`order_${orderRef.id}`);
                if (storedOrder) {
                  const orderData = JSON.parse(storedOrder);
                  sessionStorage.setItem(`order_${orderRef.id}`, JSON.stringify({
                    ...orderData,
                    ...updateData
                  }));
                } else {
                  sessionStorage.setItem(`order_${orderRef.id}`, JSON.stringify({
                    id: orderRef.id,
                    ...updateData
                  }));
                }
                console.log('Updated order data saved to sessionStorage as fallback');
              } catch (storageError) {
                console.warn('Failed to save updated order to sessionStorage:', storageError);
              }
              
              // Set a timeout for the Firestore update operation - increased to 10 seconds
              // Use the correct collection name 'marketplace_orders' instead of 'orders'
              const updatePromise = updateDoc(doc(db, 'marketplace_orders', orderRef.id), { 
                paymentStatus: 'completed',
                status: 'processing',
                transactionId: paymentResult.transactionId || '',
                paymentDetails: paymentResult,
                updatedAt: serverTimestamp()
              });
              
              const timeoutPromise = new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Firestore update timed out')), 10000)
              );
              
              // Try to update the document, but don't wait for it to complete
              Promise.race([updatePromise, timeoutPromise])
                .then(() => console.log('Successfully updated order in Firestore'))
                .catch(error => {
                  console.warn('Failed to update order in Firestore:', error);
                  console.error('Error details:', error.message, error.stack);
                });
                
              console.log('Continuing checkout process without waiting for Firestore');
            }
          } catch (updateError) {
            console.error('Error updating order status in Firestore:', updateError);
            // Continue with checkout process even if Firestore update fails
          }
          
          // Send order confirmation email
          try {
            await sendOrderStatusEmail(formData.email, {
              orderId: orderRef.id,
              items: cart.items,
              total: getCartTotal(),
              status: 'processing'
            });
            
            // If order contains digital music, send download email
            if (hasDigitalMusic) {
              await sendDigitalDownloadEmail(formData.email, {
                orderId: orderRef.id,
                items: cart.items.filter(item => item.isDigitalDownload && item.audioFileUrl)
              });
            }
          } catch (emailError) {
            console.error('Failed to send email notification:', emailError);
            // Continue with checkout process even if email fails
          }
          
          // Clear cart
          clearCart();
          
          // Store payment status in sessionStorage for reliable tracking
          try {
            sessionStorage.setItem(`payment_status_${orderRef.id}`, JSON.stringify({
              status: 'processing',
              transactionId: paymentResult.transactionId || '',
              timestamp: new Date().toISOString(),
              hasDigitalDownloads: hasDigitalMusic
            }));
          } catch (e) {
            console.warn('Failed to store payment status in sessionStorage:', e);
          }
          
          // Store comprehensive payment status in sessionStorage for reliable tracking
          try {
            sessionStorage.setItem(`payment_status_${orderRef.id}`, JSON.stringify({
              status: 'processing',
              transactionId: paymentResult.transactionId || '',
              timestamp: new Date().toISOString(),
              hasDigitalDownloads: hasDigitalMusic,
              orderFirestoreId: orderRef.firestoreId || null,
              orderLocalId: orderRef.isFirestoreFallback ? orderRef.id : null
            }));
          } catch (e) {
            console.warn('Failed to store payment status in sessionStorage:', e);
          }
          
          // Redirect to payment processing page to wait for final confirmation
          navigate(`/marketplace/payment-processing?reference=${orderRef.id}${paymentResult.transactionId ? `&transaction_id=${paymentResult.transactionId}` : ''}`, { 
            state: { 
              orderId: orderRef.id, 
              firestoreId: orderRef.firestoreId,
              isFirestoreFallback: orderRef.isFirestoreFallback,
              hasDigitalDownloads: hasDigitalMusic,
              paymentStatus: 'processing'
            } 
          });
        } else {
          // For failed payments, try to log the failure in Firestore but don't block the process
          try {
            if (orderRef.isFirestoreFallback) {
              // For local orders, create a record of the failed payment
              const failedOrderData = {
                userId: currentUser?.uid || 'anonymous',
                items: cart.items,
                total: getCartTotal(),
                paymentMethod: 'mobile',
                mobileNumber: formData.mobileNumber,
                mobileProvider: formData.mobileProvider,
                status: 'failed',
                paymentStatus: 'failed',
                createdAt: serverTimestamp(),
                contactInfo: {
                  email: formData.email,
                  name: formData.name
                },
                localOrderId: orderRef.id,
                paymentDetails: paymentResult,
                timestamp: Date.now(),
                createdAfterFailedPayment: true
              };
              
              // Try to create a record of the failed payment, but don't wait for it
              addDoc(collection(db, 'failed_payments'), failedOrderData)
                .then(() => console.log('Recorded failed payment in Firestore'))
                .catch(error => console.warn('Failed to record payment failure in Firestore:', error));
            } else {
              // For existing Firestore documents, update with failure info
              updateDoc(doc(db, 'orders', orderRef.id), { 
                paymentStatus: 'failed',
                paymentDetails: paymentResult,
                updatedAt: serverTimestamp()
              })
                .then(() => console.log('Updated order with payment failure in Firestore'))
                .catch(error => console.warn('Failed to update order with payment failure:', error));
            }
          } catch (updateError) {
            console.error('Error recording payment failure in Firestore:', updateError);
            // Continue with checkout process even if Firestore update fails
          }
          
          // Store payment failure status in sessionStorage for reliable tracking
          try {
            sessionStorage.setItem(`payment_status_${orderRef.id}`, JSON.stringify({
              status: 'failed',
              error: paymentResult.message || 'Payment failed',
              errorCode: paymentResult.code || 'unknown',
              timestamp: new Date().toISOString()
            }));
          } catch (e) {
            console.warn('Failed to store payment failure status in sessionStorage:', e);
          }
          
          // Redirect to payment processing page with failure indication
          navigate(`/marketplace/payment-processing?reference=${orderRef.id}&error=${encodeURIComponent(paymentResult.message || 'Payment failed')}&error_code=${paymentResult.code || 'unknown'}`, { 
            state: { 
              orderId: orderRef.id, 
              error: paymentResult.message || 'Payment processing failed'
            } 
          });
        }
      } catch (paymentError) {
        console.error('Payment processing error:', paymentError);
        setPaymentStage('error');
        
        // Provide more helpful error messages based on error type
        if (paymentError.message.includes('timed out')) {
          setError(`Payment request timed out. Please check your internet connection and try again.`);
        } else if (paymentError.message.includes('Network error') || 
                   paymentError.message.includes('Failed to fetch')) {
          setError(`Network connection issue. Please check your internet and try again.`);
        } else if (paymentError.message.includes('Invalid phone number') || 
                   paymentError.message.includes('mobile') || 
                   paymentError.message.includes('number')) {
          setError(`Mobile number issue: ${paymentError.message}. Please check your mobile number format.`);
        } else {
          setError(`Payment failed: ${paymentError.message}`);
        }
        
        // Log stack trace if available
        if (paymentError.stack) {
          console.error('Error stack trace:', paymentError.stack);
        }
      }
    } catch (error) {
      console.error('Checkout error:', error);
      setPaymentStage('error');
      
      // Handle Firebase specific errors
      if (error.code === 'not-found') {
        setError('Product not found or has been removed.');
      } else if (error.code === 'permission-denied') {
        setError('You do not have permission to complete this order. Please log in and try again.');
      } else if (error.code && error.code.startsWith('firestore/')) {
        // Handle other Firestore errors gracefully
        setError(`Database error: ${error.message}. Your payment was not processed.`);
      } else {
        setError(`Failed to process your order: ${error.message}. Please try again later.`);
      }
    } finally {
      setLoading(false);
      
      // If we're still in validating or processing stage but reached finally, reset to error
      if (paymentStage === 'processing' || paymentStage === 'validating') {
        console.log('Payment process ended but state was still:', paymentStage, '- resetting to idle');
        setPaymentStage('idle');
      }
      
      // Clean up debug flag
      window.checkoutFlowActive = false;
      console.log('Checkout process complete');
    }
  };

  return (
    <Container>
      <Header>
        <Title>Checkout</Title>
      </Header>
      <CheckoutGrid>
        <Form onSubmit={handleSubmit}>
          <FormSection>
            <SectionTitle>
              <FaLock /> Contact Information
            </SectionTitle>
            
            <InputGroup>
              <Label>Full Name</Label>
              <Input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter your full name"
                required
                error={!!validationErrors.name}
              />
              {validationErrors.name && (
                <ValidationError>{validationErrors.name}</ValidationError>
              )}
            </InputGroup>
            
            <InputGroup>
              <Label>Email Address</Label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email address"
                required
                error={!!validationErrors.email}
              />
              {validationErrors.email && (
                <ValidationError>{validationErrors.email}</ValidationError>
              )}
              <small style={{ color: '#aaa', marginTop: '0.25rem' }}>
                Your email will be used to send order confirmations, updates, and MP3 downloads for music purchases
              </small>
            </InputGroup>
          </FormSection>

          <FormSection>
            <SectionTitle>
              <FaLock /> Mobile Money Payment
            </SectionTitle>
            
            <InputGroup>
              <Label>Mobile Money Number</Label>
              <Input
                type="tel"
                name="mobileNumber"
                value={formData.mobileNumber}
                onChange={handleInputChange}
                placeholder="Enter your mobile money number (e.g., 0771234567)"
                required
                error={!!validationErrors.mobileNumber}
              />
              {validationErrors.mobileNumber && (
                <ValidationError>{validationErrors.mobileNumber}</ValidationError>
              )}
              <small style={{ color: '#aaa', marginTop: '0.25rem', display: 'block' }}>
                Format: 0771234567 or 256771234567
              </small>
            </InputGroup>
            

            
            <InputGroup>
              <Label>Mobile Provider</Label>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <PaymentMethod
                  type="button"
                  active={formData.mobileProvider === 'mtn'}
                  onClick={() => setFormData({...formData, mobileProvider: 'mtn'})}
                  style={{ flex: 1 }}
                >
                  MTN Mobile Money
                </PaymentMethod>
                <PaymentMethod
                  type="button"
                  active={formData.mobileProvider === 'airtel'}
                  onClick={() => setFormData({...formData, mobileProvider: 'airtel'})}
                  style={{ flex: 1 }}
                >
                  Airtel Money
                </PaymentMethod>
              </div>
            </InputGroup>
            
            <PlaceOrderButton
              type="submit"
              disabled={loading}
              whileHover={!loading ? { scale: 1.02 } : {}}
              whileTap={!loading ? { scale: 0.98 } : {}}
              style={{
                marginTop: '2rem',
                background: loading ? '#357abd' : '#4a90e2',
                opacity: loading ? 0.8 : 1
              }}
            >
              <FaLock />
              {loading ? (
  paymentStage === 'validating' ? 'Validating...' :
  paymentStage === 'processing' ? 'Processing Payment...' : 'Processing...'
) : 'Place Order'}
            </PlaceOrderButton>
          </FormSection>

          {error && (
  <motion.div
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -10 }}
    style={{ marginBottom: '1.5rem' }}
  >
    <ErrorBanner role="alert" tabIndex={-1}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <FaLock style={{ color: '#ff4d4d', fontSize: 20 }} />
        <span style={{ flex: 1 }}>
          <strong>Payment Error:</strong> {error}
          <ul style={{ margin: '0.5em 0 0 1em', color: '#fff', fontSize: '0.95em', paddingLeft: 18 }}>
            <li>Double-check your mobile number (must start with 0 or 256, no spaces or special characters).</li>
            <li>Ensure your mobile wallet has enough funds and is not blocked.</li>
            <li>Make sure your mobile money account is registered and active.</li>
            <li>If the issue persists, try again later or contact support at wilbert@kihembo.com.</li>
          </ul>
        </span>
        <button
          onClick={() => setError('')}
          aria-label="Dismiss error"
          style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', marginLeft: 8 }}
        >
          &times;
        </button>
      </div>
    </ErrorBanner>
  </motion.div>
)}
        </Form>

        <OrderSummary>
          <SectionTitle>Order Summary</SectionTitle>
          <SummaryItem>
            <span>Subtotal</span>
            <span>{getCartTotal().toLocaleString('en-UG')} {CURRENCY}</span>
          </SummaryItem>
          <SummaryItem>
            <span>Shipping</span>
            <span>Free</span>
          </SummaryItem>
          <SummaryItem total>
            <span>Total</span>
            <span>{getCartTotal().toLocaleString('en-UG')} {CURRENCY}</span>
          </SummaryItem>
        </OrderSummary>
      </CheckoutGrid>
    </Container>
  );
};

export default Checkout;
